import React from 'react';
import { User, MessageCircle, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import Table from '../../../../components/Table';
import Alert from '../../../../components/Alert';
import { Lead, LeadFilters } from '../hooks/useLeadsData';
import { formatBirthdate } from '../utils/dateUtils';

interface LeadsTableProps {
  leads: Lead[];
  loading: boolean;
  filters: LeadFilters;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  onRowClick: (lead: Lead) => void;
  onOpenWhatsApp: (whatsapp: string) => void;
  formatWhatsApp: (whatsapp: string) => string;
  onFilterChange: (field: keyof LeadFilters, value: any) => void;
}

const LeadsTable: React.FC<LeadsTableProps> = ({
  leads,
  loading,
  filters,
  pagination,
  onRowClick,
  onOpenWhatsApp,
  formatWhatsApp,
  onFilterChange
}) => {
  const columns = [
    {
      header: 'Nome',
      accessor: (lead: Lead) => (
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-bordo/10 border border-bordo/20 rounded-full flex items-center justify-center">
            <User size={14} className="text-bordo" />
          </div>
          <span className="font-semibold text-tinta">{lead.customer_name}</span>
        </div>
      )
    },
    {
      header: 'WhatsApp',
      accessor: (lead: Lead) => lead.whatsapp ? (
        <div className="flex items-center space-x-2">
          <span className="text-sm text-tinta">{formatWhatsApp(lead.whatsapp)}</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onOpenWhatsApp(lead.whatsapp);
            }}
            className="text-musgo hover:text-musgo-dark transition-colors"
            title="Abrir no WhatsApp"
          >
            <MessageCircle size={16} />
          </button>
        </div>
      ) : <span className="text-tinta-soft">-</span>
    },
    {
      header: 'Data de Nascimento',
      accessor: (lead: Lead) => (
        <span className="text-sm text-tinta-soft">
          {lead.customer_birthdate ? formatBirthdate(lead.customer_birthdate) : '-'}
        </span>
      )
    },
    {
      header: 'Sorteio',
      accessor: (lead: Lead) => (
        <span className="px-2 py-1 text-xs font-semibold bg-bordo/8 text-bordo border border-bordo/20 rounded-full">
          {lead.wheel_name || 'Sorteio Manual'}
        </span>
      )
    },
    {
      header: 'Produto Ganho',
      accessor: (lead: Lead) => {
        if (!lead.won) {
          return (
            <div className="flex items-center space-x-2">
              <span className="w-2 h-2 bg-tinta-soft/40 rounded-full"></span>
              <span className="text-tinta-soft text-sm italic">Não ganhou</span>
            </div>
          );
        }

        // Múltiplos produtos (sorteio agendado)
        if (lead.product_ids && lead.product_ids.length > 1) {
          return (
            <div className="flex flex-col gap-1">
              <div className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-musgo rounded-full"></span>
                <span className="font-semibold text-musgo text-sm">
                  {lead.product_ids.length} prêmios
                </span>
              </div>
              <span className="text-xs text-tinta-soft italic">
                Clique para ver todos
              </span>
            </div>
          );
        }

        // Produto único (instant draw)
        return (
          <div className="flex items-center space-x-2">
            <span className="w-2 h-2 bg-musgo rounded-full"></span>
            <span className="text-musgo text-sm font-medium">{lead.product_name}</span>
          </div>
        );
      }
    },
    {
      header: 'Status',
      accessor: (lead: Lead) => (
        <span className={`px-2 py-1 text-xs font-bold uppercase tracking-wider rounded-full border ${lead.won
          ? 'bg-musgo/15 text-musgo border-musgo/30'
          : 'bg-tinta/8 text-tinta-soft border-tinta/15'
          }`}>
          {lead.won ? 'Ganhou' : 'Não Ganhou'}
        </span>
      )
    },
    {
      header: 'Dados Adicionais',
      accessor: (lead: Lead) => {
        if (!lead.custom_data || Object.keys(lead.custom_data).length === 0) {
          return <span className="text-xs text-tinta-soft/60">-</span>;
        }

        const customFields = lead.custom_fields || [];
        const dataEntries = Object.entries(lead.custom_data)
          .map(([fieldId, value]) => {
            const field = customFields.find((f: any) => f.id === fieldId);
            const label = field?.label || fieldId;
            return { label, value };
          })
          .filter(entry => entry.value);

        if (dataEntries.length === 0) {
          return <span className="text-xs text-tinta-soft/60">-</span>;
        }

        return (
          <div className="text-xs text-tinta-soft space-y-0.5">
            {dataEntries.slice(0, 2).map((entry, idx) => (
              <div key={idx} className="truncate max-w-[150px]" title={`${entry.label}: ${entry.value}`}>
                <span className="font-bold text-tinta">{entry.label}:</span> {String(entry.value)}
              </div>
            ))}
            {dataEntries.length > 2 && (
              <div className="text-tinta-soft/70 italic">
                +{dataEntries.length - 2} mais
              </div>
            )}
          </div>
        );
      }
    },
    {
      header: 'Data',
      accessor: (lead: Lead) => (
        <div className="flex items-center space-x-1 text-sm text-tinta-soft">
          <Calendar size={14} />
          <span>{new Date(lead.created_at).toLocaleDateString('pt-BR')}</span>
        </div>
      )
    }
  ];

  return (
    <>
      {/* Info quando nenhuma roda está selecionada */}
      {!filters.wheel_id && (
        <Alert
          type="info"
          message="Para ver todos os leads, selecione uma roda específica nos filtros. Por questões de performance, estamos mostrando apenas uma amostra quando 'Todas as roda de prêmios' está selecionado."
        />
      )}

      {/* Tabela */}
      <div className="bg-white rounded-xl shadow-sm border border-tinta/10 overflow-hidden">
        <div className="px-6 py-4 border-b border-tinta/10 bg-creme-light/50">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
            <h3 className="font-display text-lg font-bold text-tinta">
              Lista de Clientes <span className="text-tinta-soft font-body font-normal">({pagination.total.toLocaleString()})</span>
            </h3>
            <div className="text-xs text-tinta-soft italic">
              {filters.wheel_id ? (
                `Página ${pagination.page} de ${pagination.totalPages} — ${pagination.limit} leads por página`
              ) : (
                `Amostra de leads de múltiplos sorteios — Página ${pagination.page} de ${pagination.totalPages}`
              )}
            </div>
          </div>
        </div>

        <div className="p-6">
          <Table
            data={leads}
            columns={columns}
            loading={loading}
            emptyMessage="Nenhum lead encontrado para os filtros selecionados"
            onRowClick={onRowClick}
          />

          {/* Paginação */}
          {pagination.totalPages > 1 && (
            <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              {/* Informações e Limite por página */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 text-sm text-tinta-soft w-full sm:w-auto">
                <span className="whitespace-nowrap">
                  Mostrando <span className="font-bold text-tinta">{((pagination.page - 1) * pagination.limit) + 1}</span> a{' '}
                  <span className="font-bold text-tinta">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> de{' '}
                  <span className="font-bold text-tinta">{pagination.total}</span> leads
                </span>

                <div className="flex items-center gap-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-tinta whitespace-nowrap">
                    Por página:
                  </label>
                  <select
                    value={filters.limit}
                    onChange={(e) => onFilterChange('limit', parseInt(e.target.value))}
                    className="bg-white border border-tinta/15 rounded-lg px-2 py-1 text-sm text-tinta focus:outline-none focus:ring-2 focus:ring-bordo/40 focus:border-bordo transition-colors"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>

              {/* Botões de navegação */}
              <div className="flex items-center gap-2 w-full sm:w-auto justify-center sm:justify-end">
                {/* Botão Anterior */}
                <button
                  onClick={() => onFilterChange('page', pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="inline-flex items-center px-3 py-2 border border-tinta/15 rounded-lg text-sm font-bold text-tinta-soft bg-white hover:bg-creme-light hover:text-tinta disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Anterior</span>
                </button>

                {/* Números das páginas */}
                <div className="flex items-center gap-1 overflow-x-auto">
                  {/* Primeira página */}
                  {pagination.totalPages > 5 && pagination.page > 3 && (
                    <>
                      <button
                        onClick={() => onFilterChange('page', 1)}
                        className="px-3 py-2 text-sm font-bold rounded-lg transition-colors text-tinta-soft hover:bg-creme-light hover:text-tinta border border-tinta/15 flex-shrink-0"
                      >
                        1
                      </button>
                      {pagination.page > 4 && (
                        <span className="px-2 text-tinta-soft/60 flex-shrink-0">…</span>
                      )}
                    </>
                  )}

                  {/* Páginas próximas */}
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => onFilterChange('page', pageNum)}
                        className={`px-3 py-2 text-sm font-bold rounded-lg transition-colors flex-shrink-0 border ${pageNum === pagination.page
                          ? 'bg-bordo text-creme border-bordo'
                          : 'text-tinta-soft hover:bg-creme-light hover:text-tinta border-tinta/15'
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  {/* Última página */}
                  {pagination.totalPages > 5 && pagination.page < pagination.totalPages - 2 && (
                    <>
                      {pagination.page < pagination.totalPages - 3 && (
                        <span className="px-2 text-tinta-soft/60 flex-shrink-0">…</span>
                      )}
                      <button
                        onClick={() => onFilterChange('page', pagination.totalPages)}
                        className="px-3 py-2 text-sm font-bold rounded-lg transition-colors text-tinta-soft hover:bg-creme-light hover:text-tinta border border-tinta/15 flex-shrink-0"
                      >
                        {pagination.totalPages}
                      </button>
                    </>
                  )}
                </div>

                {/* Botão Próximo */}
                <button
                  onClick={() => onFilterChange('page', pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  className="inline-flex items-center px-3 py-2 border border-tinta/15 rounded-lg text-sm font-bold text-tinta-soft bg-white hover:bg-creme-light hover:text-tinta disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <span className="hidden sm:inline">Próxima</span>
                  <ChevronRight className="h-4 w-4 sm:ml-1" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default LeadsTable;
